import React from 'react';
import FormField from './FormField';
import RadioGroup from './RadioGroup';
import CheckboxGroup from './CheckboxGroup';
import HeightInput from './HeightInput';
import { FormSchemaQuestion } from '../types';

interface DynamicFormFieldProps {
  question: FormSchemaQuestion;
  value: any;
  onChange: (value: any) => void;
  onValidate?: (fieldName: string, value: any, isRequired: boolean) => string | null;
  error?: string;
  disabled?: boolean;
  formData: Record<string, any>; // For conditional logic evaluation and height field
  onNavigateToSection?: (sectionNumber: number) => void;
}

const DynamicFormField: React.FC<DynamicFormFieldProps> = ({
  question,
  value,
  onChange,
  onValidate,
  error,
  disabled = false,
  formData,
  onNavigateToSection,
}) => {
  // Check if this field should be visible based on conditional rules
  const shouldShow = React.useMemo(() => {
    if (question.conditional_rules.length === 0) {
      return true; // Always show if no conditions
    }

    // Check all conditional rules - field shows if ANY rule is satisfied
    return question.conditional_rules.some((rule) => {
      const dependsOnValue = formData[rule.depends_on_field];

      switch (rule.condition_type) {
        case 'equals':
          return dependsOnValue === rule.condition_value;
        
        case 'not_equals':
          return dependsOnValue !== rule.condition_value;
        
        case 'in':
          const inValues = Array.isArray(rule.condition_value) 
            ? rule.condition_value 
            : [rule.condition_value];
          return inValues.includes(dependsOnValue);
        
        case 'not_in':
          const notInValues = Array.isArray(rule.condition_value) 
            ? rule.condition_value 
            : [rule.condition_value];
          return !notInValues.includes(dependsOnValue);
        
        case 'contains':
          // For checkbox/array fields
          if (Array.isArray(dependsOnValue)) {
            return dependsOnValue.includes(rule.condition_value);
          }
          return dependsOnValue === rule.condition_value;
        
        case 'not_contains':
          if (Array.isArray(dependsOnValue)) {
            return !dependsOnValue.includes(rule.condition_value);
          }
          return dependsOnValue !== rule.condition_value;
        
        default:
          return true;
      }
    });
  }, [question.conditional_rules, formData]);

  // Don't render if conditional rules say it shouldn't be shown
  if (!shouldShow) {
    return null;
  }

  const handleValidate = (fieldName: string, val: any, isRequired: boolean) => {
    if (onValidate) {
      return onValidate(fieldName, val, isRequired);
    }
    return null;
  };

  // Render based on question type
  switch (question.type) {
    case 'text':
    case 'email':
    case 'tel':
      return (
        <FormField
          label={question.label}
          name={question.field_name}
          type={question.type}
          value={value || ''}
          onChange={onChange}
          onValidate={handleValidate}
          required={question.is_required}
          placeholder={question.placeholder || undefined}
          error={error}
          minLength={question.validation_rules?.minLength}
          maxLength={question.validation_rules?.maxLength}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <FormField
          label={question.label}
          name={question.field_name}
          type="number"
          value={value || ''}
          onChange={(v) => onChange(v === '' ? '' : parseFloat(v) || 0)}
          onValidate={handleValidate}
          required={question.is_required}
          placeholder={question.placeholder || undefined}
          error={error}
          min={question.validation_rules?.min}
          max={question.validation_rules?.max}
          step={question.validation_rules?.step}
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <div className="form-question">
          <label htmlFor={question.field_name} className="question-label">
            {question.label} {question.is_required && <span className="required">*</span>}
          </label>
          {question.help_text && (
            <p className="question-help" style={{ fontSize: '0.875rem', color: '#666', marginBottom: '8px' }}>
              {question.help_text}
            </p>
          )}
          <textarea
            id={question.field_name}
            name={question.field_name}
            className={`form-input ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
            rows={6}
            value={value || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              if (!question.validation_rules?.maxLength || newValue.length <= question.validation_rules.maxLength) {
                onChange(newValue);
              }
            }}
            required={question.is_required}
            placeholder={question.placeholder || undefined}
            maxLength={question.validation_rules?.maxLength}
            disabled={disabled}
            aria-describedby={error ? `${question.field_name}-error` : undefined}
            style={{
              width: '100%',
              padding: '12px',
              border: error ? '2px solid #FF0000' : '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '1rem',
              fontFamily: 'inherit',
              resize: 'vertical',
              backgroundColor: disabled ? '#f5f5f5' : '#fff',
              cursor: disabled ? 'not-allowed' : 'text',
            }}
          />
          {question.validation_rules?.maxLength && (
            <div style={{ marginTop: '8px', fontSize: '0.875rem', color: '#666' }}>
              {(value || '').length} / {question.validation_rules.maxLength} characters
            </div>
          )}
          {error && (
            <span className="error-message" id={`${question.field_name}-error`} role="alert">
              {error}
            </span>
          )}
        </div>
      );

    case 'radio':
      return (
        <RadioGroup
          name={question.field_name}
          label={question.label}
          options={question.options.map(opt => ({
            value: opt.value,
            label: opt.label,
          }))}
          value={value || ''}
          onChange={(v) => {
            onChange(v);
            // Navigate to Section 5 (Comments) when "Others" is selected for main_issue
            if (question.field_name === 'main_issue' && v === 'others' && onNavigateToSection) {
              setTimeout(() => {
                onNavigateToSection(5);
              }, 100);
            }
          }}
          onValidate={handleValidate}
          required={question.is_required}
          error={error}
          disabled={disabled}
        />
      );

    case 'checkbox':
      return (
        <CheckboxGroup
          name={question.field_name}
          label={question.label}
          options={question.options.map(opt => ({
            value: opt.value,
            label: opt.label,
            exclusive: opt.is_exclusive,
          }))}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          onValidate={handleValidate}
          required={question.is_required}
          error={error}
          disabled={disabled}
        />
      );

    case 'height':
      return (
        <HeightInput
          valueCm={formData.height_cm}
          valueFeet={formData.height_feet}
          valueInches={formData.height_inches}
          onChange={(data) => {
            onChange(data);
          }}
          error={error}
          required={question.is_required}
          disabled={disabled}
        />
      );

    default:
      return (
        <div className="form-question">
          <label className="question-label">
            {question.label} {question.is_required && <span className="required">*</span>}
          </label>
          <p style={{ color: '#ff0000' }}>Unknown field type: {question.type}</p>
        </div>
      );
  }
};

export default DynamicFormField;

